/**
 * Books shared layout: sidebar + top bar. Uses its own visual identity so it
 * feels like a distinct product ("Emerald Books"), but shares the underlying
 * design system (colors, fonts) with Emerald.
 */
import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, BookOpen, ClipboardList, ScrollText, PieChart,
  LogOut, Building2, ChevronRight, Users2, FileText, Receipt, Wallet, CreditCard,
  Package, RefreshCw, Layers, FileCheck, Upload, Repeat, Landmark, FileSpreadsheet,
  BarChart2, Building, Users, KeyRound, Bell, Shield, Webhook, DownloadCloud, Coins,
  Menu, X, Home, MessageSquare, Ticket, UserCircle2, FileBadge, Palmtree,
  CalendarDays, ListChecks, Network, GitMerge, Lock, Gavel,
} from 'lucide-react';
import { clearBooksSession, getBooksTenant, getBooksUser } from './api';
import TourController from '../components/TourController';

const NAV = [
  { to: '/books/app', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/books/app/me', label: 'My Workspace', icon: Home },
  {
    header: 'CRM',
    children: [
      { to: '/books/app/crm/tasks', label: 'Tasks', icon: ListChecks },
      { to: '/books/app/crm/tickets', label: 'Tickets', icon: Ticket },
    ],
  },
  {
    header: 'HR',
    children: [
      { to: '/books/app/hr/employees', label: 'Employees', icon: UserCircle2 },
      { to: '/books/app/hr/policies', label: 'Policies', icon: FileBadge },
      { to: '/books/app/hr/org-chart', label: 'Org Chart', icon: Network },
    ],
  },
  {
    header: 'Leave',
    children: [
      { to: '/books/app/leave/requests', label: 'Requests', icon: Palmtree },
      { to: '/books/app/leave/balance', label: 'Balance', icon: CalendarDays },
      { to: '/books/app/leave/types', label: 'Types', icon: Layers },
    ],
  },
  {
    header: 'Sales',
    children: [
      { to: '/books/app/customers', label: 'Customers', icon: Users2 },
      { to: '/books/app/quotes', label: 'Quotes', icon: FileSpreadsheet },
      { to: '/books/app/invoices', label: 'Invoices', icon: FileText },
      { to: '/books/app/recurring', label: 'Recurring', icon: Repeat },
      { to: '/books/app/reports/aging-receivables', label: 'Debtors Aging', icon: Receipt },
      { to: '/books/app/statements', label: 'Statements', icon: ScrollText },
    ],
  },
  {
    header: 'Purchases',
    children: [
      { to: '/books/app/vendors', label: 'Vendors', icon: Users2 },
      { to: '/books/app/purchase-orders', label: 'Purchase Orders', icon: FileSpreadsheet },
      { to: '/books/app/bills', label: 'Bills', icon: FileText },
      { to: '/books/app/expenses', label: 'Expense Claims', icon: Wallet },
      { to: '/books/app/reports/aging-payables', label: 'Creditors Aging', icon: CreditCard },
    ],
  },
  {
    header: 'Inventory',
    children: [
      { to: '/books/app/inventory', label: 'Items', icon: Package },
      { to: '/books/app/stock-levels', label: 'Stock Levels', icon: BarChart2 },
    ],
  },
  {
    header: 'Banking',
    children: [
      { to: '/books/app/bank-recon', label: 'Reconciliation', icon: Landmark },
    ],
  },
  {
    header: 'Payroll',
    children: [
      { to: '/books/app/payroll/employees', label: 'Employees', icon: Users },
      { to: '/books/app/payroll/runs', label: 'Payroll Runs', icon: Coins },
    ],
  },
  {
    header: 'Ledger',
    children: [
      { to: '/books/app/accounts', label: 'Chart of Accounts', icon: BookOpen },
      { to: '/books/app/journals', label: 'Journals', icon: ClipboardList },
      { to: '/books/app/fixed-assets', label: 'Fixed Assets', icon: Package },
      { to: '/books/app/fx-rates', label: 'FX Rates', icon: RefreshCw },
      { to: '/books/app/cost-centres', label: 'Cost Centres', icon: Layers },
      { to: '/books/app/branches', label: 'Branches', icon: Building },
      { to: '/books/app/departments', label: 'Departments', icon: Building2 },
      { to: '/books/app/projects', label: 'Projects', icon: FileSpreadsheet },
    ],
  },
  {
    header: 'Reports',
    children: [
      { to: '/books/app/reports/trial-balance', label: 'Trial Balance', icon: ScrollText },
      { to: '/books/app/reports/profit-loss', label: 'Profit & Loss', icon: ScrollText },
      { to: '/books/app/reports/balance-sheet', label: 'Balance Sheet', icon: ScrollText },
      { to: '/books/app/reports/cash-flow', label: 'Cash Flow', icon: ScrollText },
      { to: '/books/app/tax-returns', label: 'Tax Returns', icon: FileCheck },
      { to: '/books/app/budgets', label: 'Budgets', icon: BarChart2 },
      { to: '/books/app/consolidation', label: 'Consolidation', icon: GitMerge },
      { to: '/books/app/rev-rec', label: 'Revenue Recognition', icon: CalendarDays },
      { to: '/books/app/reports/matrix', label: 'Matrix Report', icon: BarChart2 },
    ],
  },
  {
    header: 'Governance',
    children: [
      { to: '/books/app/approvals', label: 'Approvals', icon: Gavel },
      { to: '/books/app/period-close', label: 'Period Close', icon: Lock },
    ],
  },
  {
    header: 'Data',
    children: [
      { to: '/books/app/csv-import', label: 'CSV Import', icon: Upload },
      { to: '/books/app/backup', label: 'Backup Export', icon: DownloadCloud },
    ],
  },
  {
    header: 'Settings',
    children: [
      { to: '/books/app/billing', label: 'Billing & Plan', icon: CreditCard },
      { to: '/books/app/api-keys', label: 'API Keys', icon: KeyRound },
      { to: '/books/app/webhooks', label: 'Webhooks', icon: Webhook },
      { to: '/books/app/2fa', label: 'Two-factor Auth', icon: Shield },
    ],
  },
];

const BooksLayout = () => {
  const nav = useNavigate();
  const user = getBooksUser();
  const tenant = getBooksTenant();
  const [mobileOpen, setMobileOpen] = useState(false);

  const logout = () => {
    clearBooksSession();
    nav('/books/login');
  };
  const closeMobile = () => setMobileOpen(false);

  const NavItem = ({ item }) => (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={closeMobile}
      data-testid={`books-nav-${item.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition ${
          isActive ? 'bg-[#0f5c3d] text-white' : 'text-white/70 hover:bg-white/5 hover:text-white'
        }`
      }
    >
      <item.icon className="w-4 h-4" />
      {item.label}
    </NavLink>
  );

  const SidebarContent = () => (
    <>
      <div className="px-5 py-5 border-b border-white/10 flex items-center justify-between">
        <div>
          <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 28, fontWeight: 500, color: '#7bbf95' }}>Emerald</div>
          <div className="text-xs tracking-[0.3em] text-white/50 uppercase">Books · Accounting</div>
        </div>
        <button onClick={closeMobile} className="lg:hidden text-white/70">
          <X className="w-5 h-5" />
        </button>
      </div>
      {tenant && (
        <div className="px-5 py-3 border-b border-white/10 flex items-start gap-2">
          <Building2 className="w-4 h-4 text-[#7bbf95] mt-0.5" />
          <div className="min-w-0">
            <div className="text-sm font-medium truncate">{tenant.company_name}</div>
            <div className="text-[10px] tracking-widest uppercase text-white/50">{tenant.country_code} · {tenant.base_currency}</div>
          </div>
        </div>
      )}
      <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
        {NAV.map((n, i) =>
          n.header ? (
            <div key={i}>
              <div className="px-3 pt-4 pb-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-white/40">{n.header}</div>
              {n.children.map((c) => <NavItem key={c.to} item={c} />)}
            </div>
          ) : (
            <NavItem key={n.to} item={n} />
          )
        )}
      </nav>
      <div className="p-3 border-t border-white/10">
        {user && (
          <div className="px-3 py-2 text-xs text-white/60">
            <div className="text-white truncate">{user.name}</div>
            <div className="text-[10px] uppercase tracking-widest">{user.role}</div>
          </div>
        )}
        <button onClick={logout} data-testid="books-logout"
          className="w-full inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-white/80 hover:bg-white/5">
          <LogOut className="w-3.5 h-3.5" /> Sign out
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#e2ede6] flex">
      <aside className="hidden lg:flex flex-col w-64 bg-[#0b1a2e] text-white sticky top-0 h-screen">
        <SidebarContent />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <>
          <div className="lg:hidden fixed inset-0 bg-black/50 z-40" onClick={closeMobile} />
          <aside className="lg:hidden fixed inset-y-0 left-0 w-72 bg-[#0b1a2e] text-white z-50 flex flex-col shadow-2xl">
            <SidebarContent />
          </aside>
        </>
      )}

      <main className="flex-1 min-w-0">
        <div className="lg:hidden bg-[#0b1a2e] text-white px-4 py-3 flex items-center justify-between sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <button onClick={() => setMobileOpen(true)} data-testid="books-mobile-menu-btn" className="text-white/90">
              <Menu className="w-5 h-5" />
            </button>
            <span style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>Emerald Books</span>
          </div>
          <button onClick={logout} className="text-xs opacity-70"><LogOut className="w-4 h-4" /></button>
        </div>
        <div className="p-4 lg:p-8 max-w-6xl mx-auto pb-24 lg:pb-8">
          <Outlet />
        </div>
        {/* Mobile bottom tab bar */}
        <nav className="lg:hidden fixed bottom-0 inset-x-0 bg-white border-t z-30 grid grid-cols-5 text-[10px] uppercase tracking-wider" data-testid="books-bottom-tabs">
          <NavLink to="/books/app/me" className={({ isActive }) => `flex flex-col items-center gap-0.5 py-2 ${isActive ? 'text-[#0f5c3d]' : 'text-slate-500'}`}>
            <Home className="w-5 h-5" /><span>Home</span>
          </NavLink>
          <NavLink to="/books/app/crm/tasks" className={({ isActive }) => `flex flex-col items-center gap-0.5 py-2 ${isActive ? 'text-[#0f5c3d]' : 'text-slate-500'}`}>
            <ListChecks className="w-5 h-5" /><span>Tasks</span>
          </NavLink>
          <NavLink to="/books/app/leave/requests" className={({ isActive }) => `flex flex-col items-center gap-0.5 py-2 ${isActive ? 'text-[#0f5c3d]' : 'text-slate-500'}`}>
            <Palmtree className="w-5 h-5" /><span>Leave</span>
          </NavLink>
          <NavLink to="/books/app/crm/tickets" className={({ isActive }) => `flex flex-col items-center gap-0.5 py-2 ${isActive ? 'text-[#0f5c3d]' : 'text-slate-500'}`}>
            <Ticket className="w-5 h-5" /><span>Tickets</span>
          </NavLink>
          <NavLink to="/books/app" end className={({ isActive }) => `flex flex-col items-center gap-0.5 py-2 ${isActive ? 'text-[#0f5c3d]' : 'text-slate-500'}`}>
            <LayoutDashboard className="w-5 h-5" /><span>Books</span>
          </NavLink>
        </nav>
      </main>
      <TourController variant="books" />
    </div>
  );
};

export default BooksLayout;
